import type { IDataSource } from "@/data/dao/service/BaseServiceDao";
import { Format } from "@/data/util/Format";

import { logError } from "@/shared/logging/errors";

export interface IOptionNameFormatter<T> {
    (e: T): string;
}

export interface ISortBy<T> {
    (a: T[]): T[];
}

export class SelectBootstrap<T> {
    public onChange?: { (value: string[] | string) };

    private readonly $select: JQuery;
    private readonly isMultiSelect;
    private readonly dataSource?: IDataSource<T>;
    private readonly showAllText?: string;
    private readonly orderBy?: string;

    private nameFormatter?: IOptionNameFormatter<T>;
    private sortBy?: ISortBy<T>;
    private internalValue?: string | Array<string> = undefined;
    private keyProperty?: string;
    private isInitialized = false;
    private suppressEvents = false;

    constructor(
        $select?: JQuery,
        dataSource?: IDataSource<T>,
        formatter?: IOptionNameFormatter<T>,
        lazyInit?: boolean,
        showAllText?: string,
        keyProp?: string,
        multiSelect?: boolean,
        orderBy?: string,
        sortBy?: ISortBy<T>,
        onLoaded?: { (items: T[]) }
    ) {
        if (multiSelect !== undefined) {
            this.isMultiSelect = multiSelect;
        } else {
            this.isMultiSelect = false;
        }

        this.$select = $select === undefined ? $('<select class="form-control kt-selectpicker" />') : $select;
        if (this.isMultiSelect === true) {
            this.$select.attr("multiple", "true");
        }

        this.keyProperty = keyProp;
        this.showAllText = showAllText;
        this.dataSource = dataSource;
        this.nameFormatter = formatter;
        this.orderBy = orderBy;
        this.sortBy = sortBy;

        if ($select !== undefined) {
            this.$select.selectpicker();
            this.isInitialized = true;
        }

        this.$select.on("changed.bs.select", (e, index) => {
            this.onChangedSelect(index);
        });

        if (this.dataSource !== undefined && (lazyInit === undefined || lazyInit === false)) {
            window.setTimeout(() => {
                this.fetchData(onLoaded);
                this.$select.selectpicker("refresh");
            }, 1);
        } else {
            if (this.showAllText !== undefined) {
                if (this.$select.find('option[value="none"]').length === 0) {
                    this.$select.prepend('<option value="none">' + this.showAllText + "</option>");
                    this.$select.selectpicker("refresh");
                    const currentlySelectedVal = this.$select.find('option[selected="selected"]');
                    if (currentlySelectedVal.length === 0) this.$select.selectpicker("val", "none");
                    else this.internalValue = this.$select.selectpicker("val");

                    return;
                }
            }

            this.$select.selectpicker("refresh");
            this.internalValue = this.$select.selectpicker("val");
        }
    }

    public getValue(): string | undefined | string[] {
        if (this.isMultiSelect) return this.getValues();

        let value = this.$select.selectpicker("val");
        if (value === "none") return undefined;

        if (this.internalValue === undefined) return this.internalValue;

        if (value == null) value = this.internalValue;

        return value == null ? undefined : value;
    }

    public getValues(): Array<string> {
        const values = this.$select.selectpicker("val");

        if (this.isMultiSelect) {
            if (values.length === 1 && values.includes("none")) return [];
        }

        return values;
    }

    public getOptions(): Array<string> {
        const options: string[] = [];
        this.$select.find("option").each((i, ele) => {
            options.push($(ele).val());
        });

        return options;
    }

    public setValue(value?: string | number): SelectBootstrap<T> {
        this.suppressEvents = true;

        this.internalValue = value as any;
        if (value === undefined && this.showAllText !== undefined) value = "none";

        this.$select.selectpicker("val", value == undefined ? null : value);
        this.suppressEvents = false;

        return this;
    }

    public setValues(values: Array<string | number>): SelectBootstrap<T> {
        this.suppressEvents = true;

        const newValues = values == undefined ? [] : values;
        if (this.showAllText !== undefined && newValues.length === 0) this.$select.selectpicker("val", ["none"]);
        else this.$select.selectpicker("val", values == undefined ? [] : values);

        this.internalValue = values as Array<string>;

        this.suppressEvents = false;
        return this;
    }

    public getModel(): T | undefined {
        const v = this.getValue();
        if (v === undefined) return undefined;

        return this.$select.find('> option[value="' + v + '"]').data("model");
    }

    public setError(hasError: boolean): SelectBootstrap<T> {
        if (hasError) this.$select.addClass("is-invalid");
        else this.$select.removeClass("is-invalid");

        this.$select.selectpicker("setStyle");
        return this;
    }

    public setDisabled(isDisabled: boolean): SelectBootstrap<T> {
        this.$select.prop("disabled", isDisabled);
        this.$select.selectpicker("refresh");
        return this;
    }

    public fetchData(finished?: { (items: T[]) }, abortSignal?: AbortSignal): SelectBootstrap<T> {
        this.dataSource!.getData({ limit: 10000, orderBy: this.orderBy }, abortSignal)
            .then((entries) => {
                this.$select.empty();

                if (this.showAllText !== undefined)
                    this.$select.append('<option value="none">' + this.showAllText + "</option>");

                const items = this.sortBy !== undefined ? this.sortBy(entries.items) : entries.items;
                for (const i in items) {
                    const entry = items[i];
                    let name = this.nameFormatter !== undefined ? this.nameFormatter(entry) : undefined;
                    if (name === undefined && entry["name"] !== undefined) name = entry["name"];

                    if (name === undefined) name = "unresolved name";

                    $(
                        '<option value="' +
                            entry[this.keyProperty === undefined ? "id" : this.keyProperty] +
                            '">' +
                            Format.htmlEscape(name) +
                            "</option>"
                    )
                        .data("model", entry)
                        .appendTo(this.$select);
                }

                this.$select.selectpicker("refresh");
                if (this.internalValue !== undefined && this.internalValue != null) {
                    this.$select.selectpicker("val", this.internalValue);
                } else if (this.showAllText !== undefined)
                    this.$select.selectpicker("val", this.isMultiSelect ? [] : "none");
                else if (entries.items.length > 0) {
                    this.$select.selectpicker(
                        "val",
                        this.isMultiSelect
                            ? []
                            : entries.items[0][this.keyProperty === undefined ? "id" : this.keyProperty]
                    );
                    this.internalValue = this.isMultiSelect
                        ? []
                        : entries.items[0][this.keyProperty === undefined ? "id" : this.keyProperty];
                }

                this.$select.selectpicker("refresh");

                if (finished !== undefined) finished(items);
            })
            .catch((ex) => {
                logError(ex);
                this.$select.empty();
                if (this.showAllText !== undefined)
                    this.$select.append('<option value="none">' + this.showAllText + "</option>");

                if (finished !== undefined) finished([]);
            });

        return this;
    }

    public renderTo($element: JQuery): SelectBootstrap<T> {
        this.$select.appendTo($element);

        if (!this.isInitialized) {
            this.$select.selectpicker();
            this.isInitialized = true;
        }

        return this;
    }

    public reset(): SelectBootstrap<T> {
        if (this.showAllText !== undefined) this.setValue(undefined);
        else {
            //get first select
            this.setValue(this.$select.find("> option").first().attr("value"));
        }

        return this;
    }

    public destroy() {
        this.$select.selectpicker("destroy");
        this.$select.remove();
    }

    public static createFromSelect<T>(
        $select: JQuery,
        dataSource?: IDataSource<T>,
        formatter?: IOptionNameFormatter<T>,
        lazyInit?: boolean,
        showAllText?: string,
        keyProp?: string,
        multiSelect?: boolean
    ): SelectBootstrap<T> {
        return new SelectBootstrap($select, dataSource, formatter, lazyInit, showAllText, keyProp, multiSelect);
    }

    private onChangedSelect(index: number) {
        if (this.suppressEvents === true) return;

        let newVal = this.$select.selectpicker("val");
        if (this.isMultiSelect) {
            if (this.showAllText !== undefined) {
                this.suppressEvents = true;
                if (newVal.length === 0 || (newVal.length === 1 && newVal.includes("none")) || index === 0) {
                    newVal = ["none"];
                } else {
                    newVal = newVal.filter((c) => c !== "none");
                }
                this.$select.selectpicker("val", newVal);

                this.suppressEvents = false;
            }

            this.internalValue = newVal;
            if (this.onChange !== undefined) this.onChange(this.getValues());
        } else {
            if (newVal == null) return;

            if (newVal === "none") newVal = undefined;

            if (this.internalValue !== newVal) {
                this.internalValue = newVal;
                if (this.onChange !== undefined) this.onChange(this.getValues());
            }
        }
    }

    public static createFromSelectWithOptions<T>(
        createFromSelectOptions: CreateFromSelectOptions<T>
    ): SelectBootstrap<T> {
        return new SelectBootstrap(
            createFromSelectOptions.$select,
            createFromSelectOptions.dataSource,
            createFromSelectOptions.formatter,
            createFromSelectOptions.lazyInit,
            createFromSelectOptions.showAllText,
            createFromSelectOptions.keyProp,
            createFromSelectOptions.multiSelect,
            createFromSelectOptions.orderBy,
            createFromSelectOptions.sortBy
        );
    }
}

export type CreateFromSelectOptions<T> = {
    $select: JQuery;
    dataSource?: IDataSource<T>;
    formatter?: IOptionNameFormatter<T>;
    lazyInit?: boolean;
    showAllText?: string;
    keyProp?: string;
    multiSelect?: boolean;
    orderBy?: string;
    sortBy?: ISortBy<T>;
};
